import axios from 'axios';
import config from './config';
import { clearClientStorage } from './utils/browserStorage';
import { getLocale } from './i18n';

// Создаем экземпляр axios с базовой конфигурацией
const api = axios.create({
  baseURL: config.API_BASE_URL,
  timeout: 10000,
  withCredentials: true,
});

// Интерцептор для добавления токена авторизации
// Теперь токен хранится в HttpOnly куках, поэтому не нужно добавлять его в заголовок
// withCredentials: true в конфигурации axios обеспечивает отправку кук автоматически
api.interceptors.request.use(
  (config) => {
    // Locale for backend API messages (ru/en catalogs).
    config.headers = config.headers || {};
    config.headers['Accept-Language'] = getLocale();
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Флаг для предотвращения бесконечных циклов при обновлении токена
let isRefreshing = false;
let failedQueue = [];

// Кеш для getToken запросов - предотвращает множественные одновременные запросы
let getTokenCache = null;
let getTokenPromise = null;
let getTokenCacheTime = 0;
const GET_TOKEN_CACHE_DURATION = 600000; // Кешируем на 10 минут (токен живет 15 минут)

export const clearAuthTokenCaches = () => {
  getTokenCache = null;
  getTokenPromise = null;
  getTokenCacheTime = 0;
};

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  
  failedQueue = [];
};

// Интерцептор для обработки ошибок
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Если ошибка 401 и это не запрос на refresh, пытаемся обновить токен
    if (error.response?.status === 401 && !originalRequest._retry) {
      // Если уже идет процесс обновления токена, добавляем запрос в очередь
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then(() => api(originalRequest))
          .catch(err => {
            return Promise.reject(err);
          });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        // Пытаемся обновить токен через refresh endpoint
        // Используем прямой axios запрос без интерцепторов, чтобы избежать бесконечного цикла
        await axios.post(
          `${config.API_BASE_URL}/api/v1/refresh`,
          {},
          {
            withCredentials: true,
            timeout: 10000,
            headers: {
              'Accept-Language': getLocale(),
            },
          }
        );
        // After refresh, cookies are updated — invalidate cached bearer tokens.
        clearAuthTokenCaches();

        // Обрабатываем очередь ожидающих запросов
        processQueue(null, null);
        isRefreshing = false;
        
        // Повторяем оригинальный запрос - куки отправятся автоматически
        return api(originalRequest);
      } catch (refreshError) {
        // Если refresh не удался, редиректим на login только если это не публичный запрос
        // Куки будут удалены сервером автоматически
        processQueue(refreshError, null);
        isRefreshing = false;
        clearAuthTokenCaches();
        clearClientStorage();
        // Очищаем кеши
        getProfileCache = null;
        getProfileCacheTime = 0;
        if (typeof window !== 'undefined') {
          window._avatarCache = {};
          window._avatarPromises = {};
        }
        
        // Редиректим только если это не публичный запрос и мы не на публичной странице
        const currentPath = window.location.pathname;
        const publicPaths = ['/', '/login', '/register', '/password-forgot', '/password-reset', '/activate'];
        if (!originalRequest.skipAuthRedirect && !publicPaths.includes(currentPath)) {
          window.location.href = '/login';
        }
        
        return Promise.reject(refreshError);
      }
    }

    // Обрабатываем сетевые ошибки (ERR_EMPTY_RESPONSE, ECONNREFUSED и т.д.)
    // Если это не 401 и нет response, это сетевая ошибка
    if (!error.response && error.config) {
      // Для сетевых ошибок просто отклоняем запрос без дополнительных действий
      // Это может быть временная проблема с сетью или сервером
      // Не удаляем флаги авторизации и не делаем редирект
      return Promise.reject(error);
    }

    return Promise.reject(error);
  }
);

// API методы
export const authAPI = {
  login: (credentials) => api.post('/api/v1/login', credentials),
  register: (userData) => api.post('/api/v1/register', userData),
  logout: () => api.post('/api/v1/logout'),
  refresh: () => api.post('/api/v1/refresh'),
  getToken: (skipAuthRedirect = false) => {
    const requestConfig = skipAuthRedirect ? { skipAuthRedirect: true } : {};
    
    // Кеширование для предотвращения множественных одновременных запросов
    const now = Date.now();
    const cacheKey = skipAuthRedirect ? 'skip' : 'normal';
    
    // Если запрос уже выполняется (независимо от ключа), возвращаем тот же промис
    // Это предотвращает множественные запросы при одновременных вызовах
    if (getTokenPromise) {
      return getTokenPromise;
    }
    
    // Если есть валидный кеш, возвращаем его
    if (getTokenCache && 
        getTokenCache.key === cacheKey && 
        (now - getTokenCacheTime) < GET_TOKEN_CACHE_DURATION) {
      return Promise.resolve(getTokenCache.response);
    }
    
    // Создаем новый запрос
    getTokenPromise = api.get('/api/v1/token', requestConfig)
      .then(response => {
        // Кешируем успешный ответ
        getTokenCache = { key: cacheKey, response };
        getTokenCacheTime = now;
        return response;
      })
      .catch(error => {
        // Очищаем кеш при ошибке
        getTokenCache = null;
        getTokenCacheTime = 0;
        throw error;
      })
      .finally(() => {
        // Очищаем промис после завершения
        getTokenPromise = null;
      });
    
    return getTokenPromise;
  },
  activate: (data) => api.post('/api/v1/activate', data),
  resendActivation: (email) => api.post('/api/v1/resend-activation', { email }),
  passwordForgot: (email) => api.post('/api/v1/password-forgot', { email }),
  passwordReset: (data) => api.post('/api/v1/password-reset', data),
  passwordUpdate: (data) => api.post('/api/v1/password-update', data),
  twoFactorSetup: () => api.post('/api/v1/2fa-setup', {}, { responseType: 'blob' }),
  twoFactorEnable: (code) => api.post('/api/v1/2fa-enable', { code }),
  twoFactorDisable: () => api.post('/api/v1/2fa-disable'),
  twoFactorVerify: (code, config = {}) => api.post('/api/v1/2fa-verify', { code }, config),
};

// Кеш для getProfile запросов - предотвращает множественные одновременные запросы
let getProfileCache = null;
let getProfilePromise = null;
let getProfileCacheTime = 0;
const GET_PROFILE_CACHE_DURATION = 5000; // Кешируем на 5 секунд

// Функция для очистки кеша getProfile (используется при изменении профиля)
export const clearGetProfileCache = () => {
  getProfileCache = null;
  getProfileCacheTime = 0;
  getProfilePromise = null;
};

export const profileAPI = {
  getProfile: (skipAuthRedirect = false) => {
    const requestConfig = skipAuthRedirect ? { skipAuthRedirect: true } : {};
    
    // Кеширование для предотвращения множественных одновременных запросов
    const now = Date.now();
    const cacheKey = skipAuthRedirect ? 'skip' : 'normal';
    
    // Если запрос уже выполняется, возвращаем тот же промис
    if (getProfilePromise) {
      return getProfilePromise;
    }
    
    // Если есть валидный кеш, возвращаем его
    if (getProfileCache && 
        getProfileCache.key === cacheKey && 
        (now - getProfileCacheTime) < GET_PROFILE_CACHE_DURATION) {
      return Promise.resolve(getProfileCache.response);
    }
    
    // Создаем новый запрос
    getProfilePromise = api.get('/api/v1/profile', requestConfig)
      .then(response => {
        // Кешируем успешный ответ
        getProfileCache = { key: cacheKey, response };
        getProfileCacheTime = now;
        return response;
      })
      .catch(error => {
        // Очищаем кеш при ошибке
        getProfileCache = null;
        getProfileCacheTime = 0;
        throw error;
      })
      .finally(() => {
        // Очищаем промис после завершения
        getProfilePromise = null;
      });
    
    return getProfilePromise;
  },
  deleteProfile: () => api.delete('/api/v1/profile-delete'),
  uploadAvatar: (formData, onUploadProgress) => {
    return api.post('/api/v1/profile/avatar', formData, {
      onUploadProgress,
    });
  },
  getAvatar: (connectId = null, bypassCache = false) => {
    // Добавляем timestamp для обхода кеша браузера, если нужно
    const timestamp = bypassCache ? `&_t=${Date.now()}` : '';
    const url = connectId 
      ? `/api/v1/profile/avatar?connect_id=${connectId}${timestamp}`
      : `/api/v1/profile/avatar${timestamp ? '?' + timestamp.substring(1) : ''}`;
    
    // Кеширование для аватаров - предотвращает повторные запросы одного и того же аватара
    const cacheKey = connectId || 'self';
    const now = Date.now();
    const AVATAR_CACHE_DURATION = 300000; // Кешируем на 5 минут (аватары меняются редко)
    
    // Если нужно обойти кеш, не проверяем его
    if (!bypassCache) {
      // Проверяем кеш (используем отдельный кеш для аватаров)
      if (!window._avatarCache) {
        window._avatarCache = {};
      }
      
      const cached = window._avatarCache[cacheKey];
      if (cached && (now - cached.time) < AVATAR_CACHE_DURATION) {
        // Возвращаем закешированный ответ (клонируем arraybuffer)
        return Promise.resolve({
          data: cached.data.slice(0), // Клонируем arraybuffer
          headers: cached.headers
        });
      }
      
      // Проверяем, не выполняется ли уже запрос для этого аватара
      if (!window._avatarPromises) {
        window._avatarPromises = {};
      }
      
      if (window._avatarPromises[cacheKey]) {
        return window._avatarPromises[cacheKey];
      }
    }
    
    // Делаем запрос и кешируем результат
    const request = api.get(url, {
      responseType: 'arraybuffer', // Use arraybuffer to handle both JSON and binary data
      // Backend returns JSON with "use default avatar" message if no avatar, or image blob if avatar exists
    });
    
    // Сохраняем промис для предотвращения множественных запросов (только если не обходим кеш)
    if (!bypassCache) {
      if (!window._avatarPromises) {
        window._avatarPromises = {};
      }
      window._avatarPromises[cacheKey] = request;
    }
    
    request.then(response => {
      // Кешируем успешный ответ (только если не обходим кеш)
      if (!bypassCache) {
        if (!window._avatarCache) {
          window._avatarCache = {};
        }
        window._avatarCache[cacheKey] = {
          data: response.data.slice(0), // Клонируем arraybuffer
          headers: response.headers,
          time: now
        };
      }
      // Удаляем промис после завершения
      if (window._avatarPromises && window._avatarPromises[cacheKey]) {
        delete window._avatarPromises[cacheKey];
      }
    }).catch(() => {
      // При ошибке не кешируем и удаляем промис
      if (window._avatarPromises && window._avatarPromises[cacheKey]) {
        delete window._avatarPromises[cacheKey];
      }
    });
    
    return request;
  },
  deleteAvatar: () => api.delete('/api/v1/profile/avatar'),
  updateUserName: (userName) => api.patch('/api/v1/profile/name', { user_name: userName }),
  // Функция для очистки кеша аватара (используется после загрузки/удаления аватара)
  clearAvatarCache: (connectId = null) => {
    if (!connectId) {
      if (window._avatarCache) {
        window._avatarCache = {};
      }
      if (window._avatarPromises) {
        window._avatarPromises = {};
      }
      return;
    }
    const cacheKey = connectId;
    if (window._avatarCache && window._avatarCache[cacheKey]) {
      delete window._avatarCache[cacheKey];
    }
    // Также очищаем промис, если он есть
    if (window._avatarPromises && window._avatarPromises[cacheKey]) {
      delete window._avatarPromises[cacheKey];
    }
  },
};

export const adminAPI = {
  getOverview: () => api.get('/api/v1/admin/overview'),
  getUsers: (q) => api.get('/api/v1/admin/users', { params: q ? { q } : {} }),
  setUserActive: (userId, isActive) => api.patch(`/api/v1/admin/users/${userId}`, { is_active: isActive }),
  activateUser: (userId) => api.post(`/api/v1/admin/users/${userId}/activate`),
  reset2fa: (userId) => api.post(`/api/v1/admin/users/${userId}/reset-2fa`),
  kickUser: (userId) => api.post(`/api/v1/admin/users/${userId}/kick`),
  issueResetCode: (userId) => api.post(`/api/v1/admin/users/${userId}/reset-code`),
  banUser: (userId) => api.post(`/api/v1/admin/users/${userId}/ban`),
  deleteUser: (userId, ban = true) => api.delete(`/api/v1/admin/users/${userId}`, { params: { ban } }),
  getBans: () => api.get('/api/v1/admin/bans'),
  addBan: (email) => api.post('/api/v1/admin/bans', { email }),
  removeBan: (email) => api.delete(`/api/v1/admin/bans/${encodeURIComponent(email)}`),
  getCalls: () => api.get('/api/v1/admin/calls'),
  endCall: (callId) => api.post(`/api/v1/admin/calls/${callId}/end`),
};

export const e2eAPI = {
  registerDevice: (payload, config = {}) => api.post('/api/v1/e2e/devices', payload, config),
  listDevices: (currentDeviceId) => api.get('/api/v1/e2e/devices', {
    params: currentDeviceId ? { current_device_id: currentDeviceId } : {},
  }),
  revokeDevice: (deviceId) => api.delete(`/api/v1/e2e/devices/${encodeURIComponent(deviceId)}`),
  revokeOtherDevices: (keepDeviceId) => api.post('/api/v1/e2e/devices/revoke-others', null, {
    params: { keep_device_id: keepDeviceId },
  }),
  replenishPrekeys: (deviceId, payload, config = {}) =>
    api.put(`/api/v1/e2e/devices/${encodeURIComponent(deviceId)}/prekeys`, payload, config),
  getBundle: (connectId, params = {}) =>
    api.get(`/api/v1/e2e/users/${encodeURIComponent(connectId)}/bundle`, { params }),
  getStatus: (connectId) =>
    api.get(`/api/v1/e2e/users/${encodeURIComponent(connectId)}/status`),
  markChatOpen: (chatId) => api.post(`/api/v1/e2e/chats/${chatId}/open`),
  getChatState: (chatId) => api.get(`/api/v1/e2e/chats/${chatId}`),
  putKeystoreBackup: (blob) => api.put('/api/v1/e2e/keystore-backup', { blob }),
  getKeystoreBackup: () => api.get('/api/v1/e2e/keystore-backup'),
  postClientLogs: (events) => api.post('/api/v1/e2e/client-logs', { events }, { timeout: 8000 }),
  getClientLogs: () => api.get('/api/v1/e2e/client-logs'),
};

export const chatAPI = {
  getChats: () => api.get('/api/v1/chats'),
  getChatMessages: (chatId, limit = 20, offset = 0) => 
    api.get(`/api/v1/chats/${chatId}/messages?limit=${limit}&offset=${offset}`),
  createChatRequest: (toUserConnectId) => 
    api.post('/api/v1/chats/requests', { to_user_connect_id: toUserConnectId }),
  getChatRequests: () => api.get('/api/v1/chats/requests'),
  getOutgoingChatRequests: () => api.get('/api/v1/chats/requests/outgoing'),
  acceptChatRequest: (fromUserConnectId) => 
    api.post(`/api/v1/chats/requests/${fromUserConnectId}/accept`),
  rejectChatRequest: (fromUserConnectId) => 
    api.post(`/api/v1/chats/requests/${fromUserConnectId}/reject`),
  cancelChatRequest: (toUserConnectId) => 
    api.post(`/api/v1/chats/requests/${toUserConnectId}/cancel`),
  deleteChat: (chatId, forEveryone = false) => 
    api.delete(`/api/v1/chats/${chatId}?for_everyone=${forEveryone}`),
  deleteMessage: (chatId, messageId) => 
    api.delete(`/api/v1/chats/${chatId}/messages/${messageId}`),
  sendVoiceMessage: (chatId, formData) =>
    api.post(`/api/v1/chats/${chatId}/messages/voice`, formData, {
      timeout: 60000,
    }),
  getVoiceAudio: (url) =>
    api.get(url, { responseType: 'blob', timeout: 60000 }),
  stopChat: (chatId) => api.post(`/api/v1/chats/${chatId}/stop`),
  updateTrustSettings: (chatId, settings) => 
    api.put(`/api/v1/chats/${chatId}/trust`, settings),
  getTrustSettings: (chatId, forDisplay = false) => 
    api.get(`/api/v1/chats/${chatId}/trust?for_display=${forDisplay}`),
  createGroupChat: ({ name, member_connect_ids }) =>
    api.post('/api/v1/chats/groups', { name, member_connect_ids }),
  getGroupInvites: () => api.get('/api/v1/chats/groups/invites'),
  acceptGroupInvite: (inviteId) =>
    api.post(`/api/v1/chats/groups/invites/${inviteId}/accept`),
  rejectGroupInvite: (inviteId) =>
    api.post(`/api/v1/chats/groups/invites/${inviteId}/reject`),
  getGroupMembers: (chatId) => api.get(`/api/v1/chats/groups/${chatId}/members`),
  renameGroupChat: (chatId, name) =>
    api.patch(`/api/v1/chats/groups/${chatId}`, { name }),
  cancelGroupInvite: (inviteId) =>
    api.post(`/api/v1/chats/groups/invites/${inviteId}/cancel`),
  addGroupMembers: (chatId, member_connect_ids) =>
    api.post(`/api/v1/chats/groups/${chatId}/members`, { member_connect_ids }),
  removeGroupMember: (chatId, connectId) =>
    api.delete(`/api/v1/chats/groups/${chatId}/members/${connectId}`),
};

// WebSocket подключения
export const createWebSocket = (endpoint, token) => {
  const wsUrl = `${config.WS_BASE_URL}${endpoint}`;
  
  // Проверяем, что токен существует и не пустой
  if (token && token.trim() !== '') {
    return new WebSocket(wsUrl, ["access-token", token]);
  }
  
  return new WebSocket(wsUrl);
};

// WebSocket endpoints
export const wsEndpoints = {
  system: () => {
    return config.WS_ENDPOINTS.SYSTEM;
  },
  chat: (chatId) => {
    return `${config.WS_ENDPOINTS.CHAT}/${chatId}`;
  }
};

export default api;
